/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { StyleSheet, css } from 'aphrodite';
import { colors, mixins } from '@blend/consumer-components';
import { ProvideTimers, TimerProps } from '@blend/shared-react';
import { mediaBreakpoints } from '../../../common/responsive';
import watchLater from './watch-later.svg';

export const KBA_TIME_OUT_SECONDS = 120;
const fadedGradient = `linear-gradient(180deg, ${colors.white} 0%, ${colors.white}80 78.12%, ${colors.white}00 100%);`;

const styles = StyleSheet.create({
  timerHorizontalWrapper: {
    backgroundImage: fadedGradient,
    position: 'fixed',
    top: 0,
    width: 520,
    height: 120,
    [mediaBreakpoints.mobile]: {
      left: 0,
      right: 0,
      width: '100%',
      backgroundImage: 'unset',
    },
  },
  timerBubble: {
    ...mixins.borderRadius(4),
    ...mixins.padding(10, 18),
    marginTop: 105,
    backgroundColor: colors.reflexLight08,
    color: colors.steel,
    zIndex: 1,
    display: 'flex',
    justifyContent: 'center',
    boxShadow: '0 2px 4px 0 rgba(0, 0, 0, 0.1)',
    [mediaBreakpoints.mobile]: {
      marginTop: 0,
    },
  },
  timerClock: {
    width: 20,
    height: 20,
  },
  timerText: mixins.padding(0, 0, 0, 11),
});

export namespace Timer {
  export interface Props extends TimerProps {
    startTime: string;
    handleTimeout: () => void;
  }
}

export const _Timer: React.FC<Timer.Props> = ({ startTime, reactSafeSetInterval, handleTimeout }) => {
  const currentTime = new Date();
  // The time should always start from 2 minutes remaining if the participant first started KBA.
  // Because the backend adds a 30 seconds buffer to the 2 minutes kba requirement. So it is possible,
  // the remaining time is more than 2 minutes. Therefore, if the remaining time is greater than
  //  2 minutes, start the timer from 2 minutes.
  const timeLeft = KBA_TIME_OUT_SECONDS + 10 - Math.floor((currentTime.getTime() - Date.parse(startTime)) / 1000);
  const initialTime = Math.min(KBA_TIME_OUT_SECONDS, timeLeft);
  const timerEl = useRef<any>(null);
  const [time, setTime] = useState(initialTime);

  useEffect(() => {
    timerEl.current = reactSafeSetInterval(() => {
      setTime(prevTime => prevTime - 1);
    }, 1000);
    return () => {
      window.clearInterval(timerEl.current);
    };
  }, [startTime]);

  useEffect(() => {
    if (time <= 0) {
      setTime(0);
      window.clearInterval(timerEl.current);
      handleTimeout();
    }
  }, [time]);

  const getTimeString = useCallback(() => {
    const minutes = Math.floor(time / 60);
    const seconds = time - minutes * 60;
    return `${minutes}:${String(seconds).padStart(2, '0')}`;
  }, [time]);

  return (
    <div className={css(styles.timerHorizontalWrapper)}>
      <div className={css(styles.timerBubble)} aria-label="Timer" data-it="timer">
        <img className={css(styles.timerClock)} src={watchLater} alt="Clock With Hands" />
        <span className={css(styles.timerText)} data-it="Time-Remaining">
          {getTimeString()} Remaining
        </span>
      </div>
    </div>
  );
};

export const Timer = (props: any) => <ProvideTimers render={timerProps => <_Timer {...props} {...timerProps} />} />;
